import React, { useEffect, useMemo, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatBytes, formatDateTime, formatNumber, formatTradingDay, formatRecordTimeRange, importModeLabel } from './dailyDataUtils'
import GamingVenueCheckPanel from './GamingVenueCheckPanel'
import GamingVenueMappingForm from './GamingVenueMappingForm'
import PreviewListToolbar from './PreviewListToolbar'

function buildPreviewQuery(opts, templateId, feedId = 'patron') {
  const params = new URLSearchParams()
  if (opts.tradingDay === 'yesterday') params.set('trading_day', 'yesterday')
  else if (opts.tradingDay) params.set('trading_day', opts.tradingDay)
  if (opts.blobPath) params.set('blob_path', opts.blobPath)
  if (opts.importAllBatches) params.set('import_all_batches', 'true')
  if (opts.folderPath) params.set('folder_path', opts.folderPath)
  if (templateId) params.set('template_id', templateId)
  return params.toString()
}

export default function ImportConfirmModal({
  open,
  importOpts,
  templateId,
  feedId = 'patron',
  feedTitle = 'DimPatron',
  onClose,
  onConfirm,
  confirming,
}) {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [venueMappings, setVenueMappings] = useState({})
  const [clearBeforeImport, setClearBeforeImport] = useState(false)
  const [listSearch, setListSearch] = useState('')
  const [listPageSize, setListPageSize] = useState(15)
  const [listOffset, setListOffset] = useState(0)

  useEffect(() => {
    if (!open || !importOpts) {
      setPreview(null)
      setError(null)
      setVenueMappings({})
      setListSearch('')
      setListOffset(0)
      return undefined
    }

    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      setPreview(null)
      try {
        const qs = buildPreviewQuery(importOpts, templateId, feedId)
        const res = await authFetch(getLocalApiUrl(`/api/daily-data/${feedId}/preview?${qs}`))
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to load preview')
        if (!cancelled) setPreview(json.data)
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [open, importOpts, templateId, feedId])

  useEffect(() => {
    const unmatched = preview?.venue_check?.unmatched_file_venues || []
    if (!unmatched.length) {
      setVenueMappings({})
      return
    }
    setVenueMappings((prev) => {
      const next = {}
      for (const name of unmatched) {
        next[name] = prev[name] || ''
      }
      return next
    })
  }, [preview])

  const isPatron = feedId === 'patron'
  const isVisitor = feedId === 'visitor'
  const isVisitorApi = isVisitor && (preview?.source_type === 'api' || preview?.visitor_source === 'api')
  const previewFiles = preview?.files || []
  const sampleVisitors = preview?.sample_visitors || []
  const unmatchedVenues = preview?.venue_check?.unmatched_file_venues || []
  const needsVenueMapping = !isPatron && !isVisitor && unmatchedVenues.length > 0
  const venueMappingComplete = !needsVenueMapping
    || unmatchedVenues.every((name) => venueMappings[name])

  const listNeedle = listSearch.trim().toLowerCase()
  const filteredFiles = useMemo(() => {
    if (!listNeedle) return previewFiles
    return previewFiles.filter((f) => {
      const hay = `${f.name || ''} ${f.path || ''}`.toLowerCase()
      return hay.includes(listNeedle)
    })
  }, [previewFiles, listNeedle])
  const filteredVisitors = useMemo(() => {
    if (!listNeedle) return sampleVisitors
    return sampleVisitors.filter((v) => {
      const hay = `${v.entry_id || ''} ${v.visitor_name || ''} ${v.member_id || ''}`.toLowerCase()
      return hay.includes(listNeedle)
    })
  }, [sampleVisitors, listNeedle])
  const pagedFiles = filteredFiles.slice(listOffset, listOffset + listPageSize)
  const pagedVisitors = filteredVisitors.slice(listOffset, listOffset + listPageSize)
  const listTotal = isVisitorApi ? filteredVisitors.length : filteredFiles.length
  const listPageCount = isVisitorApi ? pagedVisitors.length : pagedFiles.length
  const listHasMore = listOffset + listPageSize < listTotal
  const showListToolbar = listTotal > 8 || !!listNeedle

  const handleVenueMappingChange = (fileName, venueId) => {
    setVenueMappings((prev) => ({ ...prev, [fileName]: venueId }))
  }

  const handleConfirm = () => {
    if (!needsVenueMapping) {
      onConfirm({ clearBeforeImport: (feedId === 'meter' || feedId === 'egm') ? clearBeforeImport : undefined })
      return
    }
    const mappings = {}
    for (const name of unmatchedVenues) {
      mappings[name] = venueMappings[name]
    }
    onConfirm({ venueMappings: mappings, clearBeforeImport: (feedId === 'meter' || feedId === 'egm') ? clearBeforeImport : undefined })
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={confirming ? undefined : onClose} aria-hidden />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Confirm {feedTitle} import</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
              <span className="ml-3 text-sm text-gray-600">Loading file summary…</span>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
          )}

          {preview && !loading && (
            <>
              {!preview.ready && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 space-y-2">
                  <p>{preview.error || 'No files found for this import.'}</p>
                  {preview.source_type === 'local' && (
                    <p>
                      {isPatron ? 'DimPatron' : feedTitle} for psgdev should come from <strong>Azure SID blob</strong>, not a folder on this VM.
                      Open{' '}
                      <a href="/monitor-safe/daily-data/settings" className="underline font-medium">Daily Data settings</a>
                      {' '}→ set <strong>File source</strong> to <strong>Azure SID blob</strong>, save, then use the
                      Azure file list on this page (or pick a trading day that has blobs).
                    </p>
                  )}
                  {preview.source_type === 'azure_blob' && (
                    <div className="space-y-2">
                      <p>
                        The <strong>Recent trading days</strong> table is a calendar view — not every date has an Azure extract.
                        Use <strong>Preview</strong> / <strong>Ingest</strong> on a file in the Azure list, or pick a date below.
                      </p>
                      {(preview.available_trading_days || []).length > 0 && (
                        <div className="rounded border border-gray-200 bg-white p-3 text-xs">
                          <p className="font-medium text-gray-700 mb-1">Days with {feedTitle} in Azure:</p>
                          <ul className="space-y-1">
                            {preview.available_trading_days.map(d => (
                              <li key={d.trading_day} className="font-mono text-gray-600">
                                {d.trading_day} — {d.folder_name} ({d.file_count} file{d.file_count === 1 ? '' : 's'})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Trading day</dt>
                  <dd className="font-medium text-gray-900">{formatTradingDay(preview.trading_day)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Import mode</dt>
                  <dd className="font-medium text-gray-900">{importModeLabel(preview.import_mode)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Source</dt>
                  <dd className="font-medium text-gray-900">
                    {preview.source_type === 'azure_blob'
                      ? 'Azure SID blob'
                      : preview.source_type === 'api'
                        ? 'RXConnect API'
                        : 'Local directory'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Template</dt>
                  <dd className="font-medium text-gray-900">{preview.template_name || 'Platform default'}</dd>
                </div>
                {isPatron && (
                  <>
                    <div>
                      <dt className="text-gray-500">Venue code</dt>
                      <dd className="font-medium text-gray-900">{preview.venue_code || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Archived members</dt>
                      <dd className="font-medium text-gray-900">{preview.import_archived_members ? 'Included' : 'Skipped'}</dd>
                    </div>
                  </>
                )}
                {isPatron && preview.import_strategy === 'insert_only' && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">Strategy</dt>
                    <dd className="font-medium text-gray-900">
                      New patrons only — existing member IDs are skipped (no sync from DimPatron)
                    </dd>
                  </div>
                )}
                {isPatron && preview.import_strategy === 'sync_existing' && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">Strategy</dt>
                    <dd className="font-medium text-gray-900">
                      New patrons are added; existing patrons sync membership, expiry, tier, email, and venue phone from the file. Payout-captured mobile numbers are not changed.
                    </dd>
                  </div>
                )}
              </dl>

              {preview.source_type === 'azure_blob' && preview.azure && (
                <p className="text-xs text-gray-500 font-mono">
                  {preview.azure.container}/{preview.azure.prefix}
                </p>
              )}
              {preview.local_directory && (
                <p className="text-xs text-gray-500 font-mono">{preview.local_directory}</p>
              )}

              {!isPatron && !isVisitor && preview.venue_check && (
                <>
                  <GamingVenueMappingForm
                    venueCheck={preview.venue_check}
                    venueMappings={venueMappings}
                    onChange={handleVenueMappingChange}
                  />
                  {!needsVenueMapping && (
                    <GamingVenueCheckPanel venueCheck={preview.venue_check} />
                  )}
                </>
              )}

              {preview.ready && (
                <>
                  {isVisitorApi ? (
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">API window</div>
                          <div className="font-mono text-xs mt-1">
                            {preview.window
                              ? `${preview.window.fromDateTime} → ${preview.window.toDateTime}`
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Sample records</div>
                          <div className="text-lg font-semibold text-gray-900 mt-1">
                            {preview.summary?.total_count ?? preview.sample_visitors?.length ?? 0}
                            {preview.has_more ? '+' : ''}
                          </div>
                        </div>
                      </div>
                      {preview.summary?.description && (
                        <p className="text-xs text-gray-600">{preview.summary.description}</p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-semibold text-gray-900">{preview.summary?.file_count ?? previewFiles.length}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">File(s)</div>
                      </div>
                      <div>
                        <div className="text-2xl font-semibold text-gray-900">{formatBytes(preview.summary?.total_bytes)}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Total size</div>
                      </div>
                      <div>
                        <div className="text-2xl font-semibold text-gray-900">
                          {preview.summary?.total_estimated_rows != null
                            ? `~${formatNumber(preview.summary.total_estimated_rows)}`
                            : '—'}
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Est. rows</div>
                      </div>
                      {!isPatron && !isVisitor && (
                        <div className="sm:col-span-1 col-span-2">
                          <div className="text-sm font-semibold text-gray-900 leading-snug">
                            {formatRecordTimeRange(
                              preview.summary?.record_time_first,
                              preview.summary?.record_time_last,
                            )}
                          </div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Record coverage</div>
                        </div>
                      )}
                    </div>
                  )}

                  {isPatron && preview.import_strategy === 'insert_only' && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
                      {importOpts?.importAllBatches
                        ? 'Files are imported one at a time with live progress. You can stop after the current file finishes. Only new member IDs are added; existing patrons are skipped.'
                        : 'DimPatron is not mirrored into PaySafeGO. Rows already in your patron list are skipped; only new member IDs are added.'}
                    </p>
                  )}
                  {isPatron && preview.import_strategy === 'sync_existing' && (
                    <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded p-2">
                      {importOpts?.importAllBatches
                        ? 'Files import one at a time. New patrons are added; existing patrons get membership status, expiry, tier, email, and venue directory phone updated. Payout mobile capture stays on payout records only.'
                        : 'New patrons are added. Existing patrons sync membership, expiry, tier, email, and venue phone from DimPatron. Name, address, and compliance fields are preserved.'}
                    </p>
                  )}

                  {preview.summary.rows_estimated_from_size && (
                    <p className="text-xs text-gray-500">
                      Row counts are estimated from file size (~{Math.round(280)} bytes/row) — files are not loaded into memory for preview.
                    </p>
                  )}

                  {preview.summary.row_counts_partial && !preview.summary.rows_estimated_from_size && (
                    <p className="text-xs text-amber-700">
                      Row counts omitted for large files (&gt;20 MB). Size and file list are still shown.
                    </p>
                  )}

                  {!isPatron && !isVisitor && (
                    <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded p-2">
                      {importOpts?.importAllBatches
                        ? 'Files import one at a time with live progress. Rows are upserted into the tenant database using the configured mapping template.'
                        : 'Rows are parsed from the pipe-delimited extract and upserted into the tenant database using the configured mapping template.'}
                    </p>
                  )}

                  {isVisitor && (
                    <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded p-2">
                      {isVisitorApi
                        ? 'Visitor check-ins are fetched from RXConnect and upserted into visitor_entries. Existing entries are updated by entry ID; they are not added to the patron list.'
                        : 'DimVisitor rows are parsed and upserted into visitor_entries by entry ID. They are not added to the patron list.'}
                    </p>
                  )}

                  {isVisitorApi && sampleVisitors.length > 0 && (
                    <div className="space-y-2">
                      {showListToolbar && (
                        <PreviewListToolbar
                          search={listSearch}
                          onSearchChange={(v) => { setListSearch(v); setListOffset(0) }}
                          searchPlaceholder="Search entry ID, name, member…"
                          pageSize={listPageSize}
                          onPageSizeChange={(n) => { setListPageSize(n); setListOffset(0) }}
                          offset={listOffset}
                          pageCount={listPageCount}
                          totalMatched={listTotal}
                          hasMore={listHasMore}
                          onPrev={() => setListOffset((o) => Math.max(0, o - listPageSize))}
                          onNext={() => setListOffset((o) => o + listPageSize)}
                        />
                      )}
                      <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entry ID</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entry time</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Member #</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {pagedVisitors.map((v) => (
                              <tr key={v.entry_id}>
                                <td className="px-3 py-2 font-mono text-xs">{v.entry_id}</td>
                                <td className="px-3 py-2 text-xs">{v.visitor_name || '—'}</td>
                                <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDateTime(v.entry_datetime)}</td>
                                <td className="px-3 py-2 font-mono text-xs">{v.member_id || '—'}</td>
                              </tr>
                            ))}
                            {!pagedVisitors.length && (
                              <tr>
                                <td colSpan={4} className="px-3 py-4 text-center text-xs text-gray-500">No matches</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {preview.has_more && (
                          <p className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t">
                            Sample only — import loads all pages from RXConnect.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {preview.file_fallback && (
                    <p className="text-xs text-amber-700">
                      No exact file for this trading day — will use the latest file in the import directory.
                    </p>
                  )}

                  {previewFiles.length > 0 && (
                    <div className="space-y-2">
                      {showListToolbar && (
                        <PreviewListToolbar
                          search={listSearch}
                          onSearchChange={(v) => { setListSearch(v); setListOffset(0) }}
                          searchPlaceholder="Search file name…"
                          pageSize={listPageSize}
                          onPageSizeChange={(n) => { setListPageSize(n); setListOffset(0) }}
                          offset={listOffset}
                          pageCount={listPageCount}
                          totalMatched={listTotal}
                          hasMore={listHasMore}
                          onPrev={() => setListOffset((o) => Math.max(0, o - listPageSize))}
                          onNext={() => setListOffset((o) => o + listPageSize)}
                        />
                      )}
                      <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Size</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Est. rows</th>
                              {!isPatron && !isVisitor && (
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Record coverage</th>
                              )}
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {pagedFiles.map(f => (
                              <tr key={f.path}>
                                <td className="px-3 py-2 font-mono text-xs text-gray-800 max-w-xs truncate" title={f.path}>
                                  {f.name}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">{formatBytes(f.size)}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {f.estimated_rows != null ? formatNumber(f.estimated_rows) : '—'}
                                </td>
                                {!isPatron && !isVisitor && (
                                  <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                                    {f.time_range_skipped
                                      ? '—'
                                      : formatRecordTimeRange(f.record_time_first, f.record_time_last)}
                                  </td>
                                )}
                                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                  {formatDateTime(f.last_modified)}
                                </td>
                              </tr>
                            ))}
                            {!pagedFiles.length && (
                              <tr>
                                <td colSpan={isPatron || isVisitor ? 4 : 5} className="px-3 py-4 text-center text-xs text-gray-500">No matches</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {isPatron && (
                    <p className="text-xs text-gray-500">
                      Existing members are updated by member ID; new members are inserted. Compliance fields (risk, PEP) are not overwritten.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between gap-3">
          {(feedId === 'meter' || feedId === 'egm') && preview?.ready && (
            <label className="flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                checked={clearBeforeImport}
                onChange={(e) => setClearBeforeImport(e.target.checked)}
                disabled={confirming}
                className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {feedId === 'egm'
                ? 'Clear DimMachine remap keys before import'
                : 'Clear existing meter data before import'}
            </label>
          )}
          <div className="flex gap-3 ml-auto">
            <button
              type="button"
              onClick={onClose}
              disabled={confirming}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming || loading || !preview?.ready || (needsVenueMapping && !venueMappingComplete)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {confirming ? 'Importing…' : importOpts?.importAllBatches ? 'Start batch ingest' : 'Start import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
